/* AdminSecurityPage — FASE 8.1 (v1.14.78.0)
 * Control center sicurezza admin: MFA (TOTP) + IP allowlist + access log.
 * Endpoint usati:
 *   - GET  /api/admin/security/overview          (dashboard counters)
 *   - GET  /api/auth/2fa/status                  (stato MFA per admin loggato)
 *   - POST /api/auth/2fa/setup                   (genera secret + QR + backup codes)
 *   - POST /api/auth/2fa/confirm                 (conferma con primo codice)
 *   - POST /api/auth/2fa/disable                 (richiede pwd + codice)
 *   - GET/POST /api/admin/security/ip-allowlist  (CIDR ok)
 *   - GET  /api/admin/security/access-log        (ultimi 200 log)
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  ShieldCheck, ShieldAlert, KeyRound, Globe, ListChecks,
  Plus, Trash2, Download, AlertTriangle, RefreshCcw, Lock, Unlock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axiosCfg = { withCredentials: true };

function readAdminToken() {
  try {
    return JSON.parse(localStorage.getItem("colucci_admin_session") || "{}").token || "";
  } catch (_e) { return ""; }
}
function authHeaders() {
  const tok = readAdminToken();
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

// ---------------------------------------------------------------------------
// MFA Setup wizard
// ---------------------------------------------------------------------------
function MFASetupModal({ onClose, onSuccess }) {
  const [step, setStep] = useState(1); // 1=qr, 2=verify, 3=backup
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.post(`${API}/auth/2fa/setup`, {}, { headers: authHeaders(), ...axiosCfg });
        setSetupData(r.data);
      } catch (err) {
        toast.error("Errore setup MFA: " + (err.response?.data?.detail || err.message));
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [onClose]);

  const submitConfirm = async () => {
    if (!code || code.length !== 6) {
      toast.error("Inserisci il codice a 6 cifre dall'app authenticator");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/confirm`, { code }, { headers: authHeaders(), ...axiosCfg });
      setBackupCodes(setupData?.backup_codes || []);
      setStep(3);
      toast.success("MFA attivata con successo");
    } catch (err) {
      toast.error("Codice non valido: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    const text = `COLUCCI ASSICURATORI — Backup Codes MFA Admin\n\n` +
      `Account: ${setupData?.qr_data_url ? "admin" : ""}\n` +
      `Data generazione: ${new Date().toISOString()}\n\n` +
      `IMPORTANTE: Conserva questi codici in luogo sicuro.\n` +
      `Ogni codice può essere usato UNA SOLA VOLTA per accedere\n` +
      `senza app authenticator (es. smartphone perso).\n\n` +
      backupCodes.map((c, i) => `${(i + 1).toString().padStart(2, "0")}. ${c}`).join("\n") + "\n";
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `colucci-mfa-backup-codes-${new Date().toISOString().slice(0,10)}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="mfa-setup-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E5E2DB] flex items-center justify-between">
          <h3 className="font-heading text-xl font-bold text-[#163321]">Attivazione MFA — Step {step}/3</h3>
          <button onClick={onClose} className="text-[#4A5E51] hover:text-[#163321]" data-testid="mfa-setup-close">×</button>
        </div>
        <div className="p-6 space-y-4">
          {loading && <p className="text-sm text-[#4A5E51]">Caricamento…</p>}

          {step === 1 && setupData && (
            <>
              <p className="text-sm text-[#4A5E51]">
                Scansiona il codice QR con la tua app authenticator (Google Authenticator, Authy, 1Password, Bitwarden, ecc.).
              </p>
              <div className="flex justify-center bg-[#FCFCFA] p-4 rounded-lg border border-[#E5E2DB]">
                <img src={setupData.qr_data_url} alt="QR MFA" className="w-56 h-56" data-testid="mfa-qr-code" />
              </div>
              <details className="text-xs text-[#4A5E51]">
                <summary className="cursor-pointer">Non riesci a scansionare? Inserisci manualmente</summary>
                <code className="block bg-[#FCFCFA] p-2 mt-2 break-all rounded border border-[#E5E2DB]" data-testid="mfa-secret">
                  {setupData.secret}
                </code>
              </details>
              <Button onClick={() => setStep(2)} className="w-full" data-testid="mfa-setup-next-btn">
                Ho scansionato → Avanti
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <p className="text-sm text-[#4A5E51]">
                Inserisci il codice a 6 cifre mostrato dalla tua app authenticator per confermare l&apos;attivazione.
              </p>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                className="w-full text-center text-2xl tracking-widest p-3 border-2 border-[#E5E2DB] rounded-lg font-mono"
                data-testid="mfa-verify-code"
              />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">Indietro</Button>
                <Button onClick={submitConfirm} disabled={loading || code.length !== 6} className="flex-1" data-testid="mfa-verify-submit">
                  Conferma
                </Button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
                <AlertTriangle className="text-red-600 shrink-0" size={20} />
                <div className="text-sm text-red-900">
                  <strong>Salva subito questi backup codes.</strong> Non saranno più mostrati.
                  Usa uno di questi codici per accedere se perdi il dispositivo authenticator.
                </div>
              </div>
              <div className="bg-[#FCFCFA] rounded-lg p-4 border border-[#E5E2DB] font-mono text-sm" data-testid="mfa-backup-codes">
                <div className="grid grid-cols-2 gap-2">
                  {backupCodes.map((c, i) => (
                    <div key={c} className="flex gap-2">
                      <span className="text-[#4A5E51] w-6">{i + 1}.</span>
                      <span className="text-[#163321]">{c}</span>
                    </div>
                  ))}
                </div>
              </div>
              <Button onClick={downloadBackupCodes} variant="outline" className="w-full" data-testid="mfa-download-backup">
                <Download size={16} className="mr-2" /> Scarica come file .txt
              </Button>
              <Button onClick={() => { onSuccess(); onClose(); }} className="w-full bg-[#163321]" data-testid="mfa-setup-done-btn">
                Ho salvato i codici → Chiudi
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MFA Disable modal
// ---------------------------------------------------------------------------
function MFADisableModal({ onClose, onSuccess }) {
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!password || !code) {
      toast.error("Inserisci password e codice TOTP/backup");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { password, code }, { headers: authHeaders(), ...axiosCfg });
      toast.success("MFA disattivata");
      onSuccess();
      onClose();
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="mfa-disable-modal">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-[#E5E2DB] flex items-center justify-between">
          <h3 className="font-heading text-xl font-bold text-[#163321]">Disattiva MFA</h3>
          <button onClick={onClose} className="text-[#4A5E51] hover:text-[#163321]">×</button>
        </div>
        <div className="p-6 space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
            Disattivare MFA riduce significativamente la sicurezza dell&apos;account. Procedi solo se necessario.
          </div>
          <input type="password" placeholder="Password admin" value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full p-3 border-2 border-[#E5E2DB] rounded-lg" data-testid="mfa-disable-password" />
          <input type="text" placeholder="Codice TOTP o backup" value={code} onChange={(e) => setCode(e.target.value)}
            className="w-full p-3 border-2 border-[#E5E2DB] rounded-lg font-mono" data-testid="mfa-disable-code" />
          <Button onClick={submit} disabled={loading} className="w-full bg-red-700 hover:bg-red-800" data-testid="mfa-disable-submit">
            Disattiva MFA
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function AdminSecurityPage() {
  const [tab, setTab] = useState("mfa");
  const [overview, setOverview] = useState(null);
  const [mfaStatus, setMfaStatus] = useState(null);
  const [allowlist, setAllowlist] = useState([]);
  const [accessLog, setAccessLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [newIp, setNewIp] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, ms, al, ac] = await Promise.all([
        axios.get(`${API}/admin/security/overview`, { headers: authHeaders(), ...axiosCfg }),
        axios.get(`${API}/auth/2fa/status`, { headers: authHeaders(), ...axiosCfg }),
        axios.get(`${API}/admin/security/ip-allowlist`, { headers: authHeaders(), ...axiosCfg }),
        axios.get(`${API}/admin/security/access-log?limit=100`, { headers: authHeaders(), ...axiosCfg }),
      ]);
      setOverview(ov.data);
      setMfaStatus(ms.data);
      setAllowlist(al.data?.ips || []);
      setAccessLog(ac.data?.items || []);
    } catch (err) {
      toast.error("Errore caricamento dati sicurezza: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const addIp = async () => {
    if (!newIp.trim()) return;
    const merged = [...allowlist, newIp.trim()];
    try {
      const r = await axios.post(`${API}/admin/security/ip-allowlist`, { ips: merged }, { headers: authHeaders(), ...axiosCfg });
      setAllowlist(r.data?.ips || []);
      setNewIp("");
      toast.success("IP aggiunto alla allowlist");
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const removeIp = async (ip) => {
    if (!window.confirm(`Rimuovere ${ip} dalla allowlist?`)) return;
    const next = allowlist.filter((x) => x !== ip);
    try {
      const r = await axios.post(`${API}/admin/security/ip-allowlist`, { ips: next }, { headers: authHeaders(), ...axiosCfg });
      setAllowlist(r.data?.ips || []);
      toast.success("IP rimosso");
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const mfaEnabled = mfaStatus?.enabled;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8" data-testid="admin-security-page">
      <header className="mb-6">
        <p className="text-[10px] uppercase tracking-widest text-[#4A5E51] font-semibold mb-2">Sistema · Sicurezza</p>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#163321] flex items-center gap-3">
              <ShieldCheck className="text-[#163321]" size={32} /> Sicurezza Admin
            </h1>
            <p className="text-sm text-[#4A5E51] mt-2 max-w-2xl">
              Hardening accesso amministrativo: MFA con app authenticator, IP allowlist (CIDR), audit log accessi.
            </p>
          </div>
          <Button onClick={reload} variant="outline" data-testid="security-reload-btn">
            <RefreshCcw size={16} className="mr-2" /> Aggiorna
          </Button>
        </div>
      </header>

      {/* KPI overview */}
      {overview && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <KPICard
            testid="kpi-mfa"
            icon={mfaEnabled ? <Lock className="text-emerald-700" size={20}/> : <Unlock className="text-amber-700" size={20}/>}
            label="Stato MFA"
            value={mfaEnabled ? "ATTIVA" : "NON ATTIVA"}
            subtitle={mfaEnabled ? `Backup codes rimasti: ${mfaStatus?.backup_codes_remaining || 0}` : "Configura ora per indurire login"}
            tone={mfaEnabled ? "ok" : "warn"}
          />
          <KPICard
            testid="kpi-allowlist"
            icon={<Globe className="text-[#163321]" size={20}/>}
            label="IP Allowlist"
            value={`${overview.ip_allowlist.count} regola${overview.ip_allowlist.count === 1 ? "" : "e"}`.replace("regolae", "regole")}
            subtitle={overview.ip_allowlist.count === 0 ? "Nessuna restrizione attiva" : "Login admin filtrato per IP"}
            tone={overview.ip_allowlist.count > 0 ? "ok" : "neutral"}
          />
          <KPICard
            testid="kpi-access"
            icon={<ListChecks className="text-[#163321]" size={20}/>}
            label="Accessi 24h"
            value={`${overview.access_log.success_24h} OK · ${overview.access_log.failed_24h} KO`}
            subtitle={`Totale storico: ${overview.access_log.total}`}
            tone={overview.access_log.failed_24h > 5 ? "warn" : "neutral"}
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#E5E2DB] mb-6 overflow-x-auto" data-testid="security-tabs">
        <TabBtn active={tab === "mfa"} onClick={() => setTab("mfa")} testid="security-tab-mfa">
          <KeyRound size={16} className="mr-2" /> Autenticazione MFA
        </TabBtn>
        <TabBtn active={tab === "allowlist"} onClick={() => setTab("allowlist")} testid="security-tab-allowlist">
          <Globe size={16} className="mr-2" /> IP Allowlist
        </TabBtn>
        <TabBtn active={tab === "log"} onClick={() => setTab("log")} testid="security-tab-log">
          <ListChecks size={16} className="mr-2" /> Audit Log Accessi
        </TabBtn>
      </div>

      {loading && <p className="text-sm text-[#4A5E51]">Caricamento…</p>}

      {!loading && tab === "mfa" && (
        <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6">
          <h3 className="font-heading text-xl font-bold text-[#163321] mb-2">Multi-Factor Authentication (TOTP)</h3>
          <p className="text-sm text-[#4A5E51] mb-4">
            Aggiungi un secondo fattore al login admin tramite app authenticator (Google Authenticator, Authy, 1Password, Bitwarden).
            Una volta attivata, ogni accesso richiederà un codice a 6 cifre. Riceverai 8 backup codes monouso per emergenze.
          </p>

          {mfaStatus && (
            <div className="bg-[#FCFCFA] border border-[#E5E2DB] rounded-lg p-4 mb-4" data-testid="mfa-current-status">
              <div className="flex items-center gap-3">
                {mfaEnabled ? <ShieldCheck className="text-emerald-700" size={28}/> : <ShieldAlert className="text-amber-700" size={28}/>}
                <div>
                  <p className="font-bold text-[#163321]">{mfaEnabled ? "MFA attiva" : "MFA non attiva"}</p>
                  <p className="text-xs text-[#4A5E51]">
                    {mfaEnabled
                      ? `Attivata il ${mfaStatus.activated_at?.slice(0,10) || "—"} · Backup codes residui: ${mfaStatus.backup_codes_remaining}`
                      : "Il login richiede solo email + password (raccomandata attivazione MFA)"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {!mfaEnabled && (
            <Button onClick={() => setShowSetup(true)} className="bg-[#163321]" data-testid="mfa-setup-start-btn">
              <KeyRound size={16} className="mr-2" /> Attiva MFA ora
            </Button>
          )}
          {mfaEnabled && (
            <Button onClick={() => setShowDisable(true)} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="mfa-disable-btn">
              Disattiva MFA
            </Button>
          )}
        </div>
      )}

      {!loading && tab === "allowlist" && (
        <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6">
          <h3 className="font-heading text-xl font-bold text-[#163321] mb-2">IP Allowlist Admin</h3>
          <p className="text-sm text-[#4A5E51] mb-4">
            Restringi l&apos;accesso al pannello admin a IP specifici o range CIDR. Se la lista è <strong>vuota</strong>, l&apos;accesso è consentito da qualsiasi IP.
            Esempi validi: <code className="bg-[#FCFCFA] px-1 rounded">192.168.1.100</code>, <code className="bg-[#FCFCFA] px-1 rounded">10.0.0.0/24</code>, <code className="bg-[#FCFCFA] px-1 rounded">2001:db8::/32</code>.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 text-sm text-amber-900">
            <AlertTriangle size={16} className="inline mr-1" />
            Attenzione: aggiungi prima il tuo IP corrente per evitare di bloccarti fuori. Il check viene applicato solo agli utenti con role=admin.
          </div>

          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newIp}
              onChange={(e) => setNewIp(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addIp()}
              placeholder="es. 192.168.1.0/24 o 10.0.0.5"
              className="flex-1 p-3 border-2 border-[#E5E2DB] rounded-lg"
              data-testid="allowlist-new-ip-input"
            />
            <Button onClick={addIp} disabled={!newIp.trim()} className="bg-[#163321]" data-testid="allowlist-add-btn">
              <Plus size={16} className="mr-1" /> Aggiungi
            </Button>
          </div>

          <div className="border border-[#E5E2DB] rounded-lg overflow-hidden">
            {allowlist.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#4A5E51]" data-testid="allowlist-empty">
                Nessun IP nella allowlist — accesso consentito da qualsiasi IP.
              </div>
            ) : (
              <ul className="divide-y divide-[#E5E2DB]">
                {allowlist.map((ip) => (
                  <li key={ip} className="flex items-center justify-between p-3 hover:bg-[#FCFCFA]" data-testid={`allowlist-row-${ip.replace(/[^a-z0-9]/gi, "_")}`}>
                    <code className="text-sm font-mono text-[#163321]">{ip}</code>
                    <button onClick={() => removeIp(ip)} className="text-red-600 hover:text-red-800" data-testid={`allowlist-remove-${ip.replace(/[^a-z0-9]/gi, "_")}`}>
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {!loading && tab === "log" && (
        <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6">
          <h3 className="font-heading text-xl font-bold text-[#163321] mb-4">Audit Log Accessi Admin</h3>
          <p className="text-sm text-[#4A5E51] mb-4">
            Ultimi {accessLog.length} eventi (retention 365 giorni). IP, user-agent e motivazione esiti registrati.
          </p>

          <div className="border border-[#E5E2DB] rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="access-log-table">
              <thead className="bg-[#FCFCFA] text-xs uppercase text-[#4A5E51]">
                <tr>
                  <th className="text-left p-2">Timestamp</th>
                  <th className="text-left p-2">Email</th>
                  <th className="text-left p-2">IP</th>
                  <th className="text-left p-2">Action</th>
                  <th className="text-center p-2">MFA</th>
                  <th className="text-center p-2">Esito</th>
                  <th className="text-left p-2">Note</th>
                </tr>
              </thead>
              <tbody>
                {accessLog.length === 0 ? (
                  <tr><td colSpan={7} className="p-4 text-center text-[#4A5E51]">Nessun evento registrato</td></tr>
                ) : accessLog.map((e, i) => (
                  <tr key={i} className="border-t border-[#E5E2DB] hover:bg-[#FCFCFA]">
                    <td className="p-2 font-mono text-xs">{(e.timestamp || "").replace("T", " ").slice(0, 19)}</td>
                    <td className="p-2 truncate max-w-[180px]">{e.email || "—"}</td>
                    <td className="p-2 font-mono text-xs">{e.ip || "—"}</td>
                    <td className="p-2 text-xs">{e.action || "login"}</td>
                    <td className="p-2 text-center">{e.mfa_used ? "✓" : "—"}</td>
                    <td className="p-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${e.success ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                        {e.success ? "OK" : "FAIL"}
                      </span>
                    </td>
                    <td className="p-2 text-xs text-[#4A5E51]">{(e.extra?.reason || (e.extra?.no_mfa_configured ? "no-mfa" : "")) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showSetup && <MFASetupModal onClose={() => setShowSetup(false)} onSuccess={reload} />}
      {showDisable && <MFADisableModal onClose={() => setShowDisable(false)} onSuccess={reload} />}
    </div>
  );
}

function KPICard({ icon, label, value, subtitle, tone, testid }) {
  const toneStyles = {
    ok: "border-emerald-200 bg-emerald-50",
    warn: "border-amber-200 bg-amber-50",
    neutral: "border-[#E5E2DB] bg-white",
  };
  return (
    <div className={`rounded-2xl border p-4 ${toneStyles[tone] || toneStyles.neutral}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#4A5E51] mb-1">
        {icon} {label}
      </div>
      <div className="text-2xl font-bold text-[#163321]">{value}</div>
      <div className="text-xs text-[#4A5E51] mt-1">{subtitle}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
        active
          ? "border-[#163321] text-[#163321]"
          : "border-transparent text-[#4A5E51] hover:text-[#163321]"
      }`}
    >
      {children}
    </button>
  );
}
